// src/components/ContactForm.jsx
import { useState } from "react";
import { toast } from "react-hot-toast";
import "./ContactForm.scss";

import { useRecaptcha, executeRecaptcha } from "../hooks/useRecaptcha";
import { useSendContactMessageMutation } from "../features/api/contactApiSlice";
import LoadingSpinner from "../components/loader/Loader";

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const [sendContactMessage, { isLoading }] = useSendContactMessageMutation();

  useRecaptcha(SITE_KEY, ["/contact"]); // ✅ only show badge on /contact

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields.");
      setLoading(false);
      return;
    }

    // In a real application, you would send this to your backend API
    try {
      const token = await executeRecaptcha(SITE_KEY, "contact");

      await sendContactMessage({
        ...formData,
        recaptchaToken: token,
      }).unwrap();

      console.log("Form submitted:", formData);
      toast.success("Message sent! We will get back to you shortly.");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(
        err?.data?.message || "Failed to send message. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <form className="contact-form" onSubmit={handleSubmit}>
      <h2>Send Us a Message</h2>
      <div className="form-group">
        <label htmlFor="name">Your Name</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
        />
      </div>
      <div className="form-group">
        <label htmlFor="email">Your Email</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
        />
      </div>
      <div className="form-group">
        <label htmlFor="subject">Subject</label>
        <input
          type="text"
          id="subject"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
        />
      </div>
      <div className="form-group">
        <label htmlFor="message">Your Message</label>
        <textarea
          id="message"
          name="message"
          rows="6"
          value={formData.message}
          onChange={handleChange}
          required
        ></textarea>
      </div>
      <button type="submit" disabled={loading}>
        {loading ? "Sending..." : "Send Message"}
      </button>
    </form>
  );
};

export default ContactForm;
